import { useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { FmbTestimonial, FmbTestimonialVideo } from '@/consts/frauMeetsBoerse/types';
import FmbImageSlot from '@/components/frauMeetsBoerse/FmbImageSlot';
import FmbHeroVideo from '@/components/frauMeetsBoerse/FmbHeroVideo';
import { useBunnyIsPlaying } from '@/hooks/useBunnyIsPlaying';

interface FmbTestimonialMarqueeProps {
  /** Video-Stimme — läuft als erste Karte mit */
  video: FmbTestimonialVideo;
  items: FmbTestimonial[];
  /** Sekunden pro Karte — ergibt zusammen die Dauer eines Durchlaufs */
  secondsPerItem?: number;
}

// Der Abstand steckt als Margin in der Karte (nicht als gap der Spur), damit
// die beiden Hälften exakt gleich breit sind — siehe Komponentenkommentar.
const CARD = 'm-0 mr-6 flex shrink-0 flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0_8px_28px_var(--fmb-shadow-card)]';
const MEDIA = 'w-full rounded-xl border border-[var(--fmb-border-card)]';

// Die Screenshots sind 500×650, das Video 9:16. Damit beide Kartentypen
// dieselbe Medienhöhe bekommen, ist die Videokarte um den Faktor
// (500/650) × (9/16) = 0,73125 schmaler — sonst müsste der Player im
// breiteren Rahmen mit schwarzen Balken laufen.
const IMAGE_CARD_WIDTH = 'w-[clamp(230px,24vw,282px)]';
const VIDEO_CARD_WIDTH = 'w-[clamp(168px,17.55vw,206px)]';

function CardFrame({
  children,
  caption,
  ariaHidden,
  widthClass
}: {
  children: ReactNode;
  caption: ReactNode;
  ariaHidden: boolean;
  widthClass: string;
}) {
  return (
    <figure aria-hidden={ariaHidden} className={`${CARD} ${widthClass}`}>
      {children}
      <figcaption className="flex items-center gap-3 border-t border-[var(--fmb-card-divider)] px-1 pt-4">
        {caption}
      </figcaption>
    </figure>
  );
}

function Caption({ initial, name, meta }: { initial: string; name: string; meta: string }) {
  return (
    <>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--fmb-accent)] text-[15px] font-bold text-white">
        {initial}
      </div>
      <div className="flex min-w-0 flex-col gap-px">
        <div className="truncate text-[15px] font-bold">{name}</div>
        <div className="text-[13px] leading-tight text-[var(--fmb-faint)]">{meta}</div>
      </div>
    </>
  );
}

/**
 * Endlos durchlaufende Leiste mit der Video-Stimme und den Original-Screenshots
 * der Rückmeldungen.
 *
 * Die Spur enthält alle Karten zweimal und wird um genau 50 % verschoben —
 * der Rücksprung auf den Anfang ist dadurch unsichtbar. Damit die beiden
 * Hälften exakt gleich breit sind, trägt jede Karte ihren Abstand als eigenes
 * Margin; ein `gap` der Spur würde am Übergang einen halben Abstand
 * verschlucken und sichtbar ruckeln.
 *
 * Angehalten wird die Leiste beim Hovern und bei Tastaturfokus (per CSS) sowie
 * solange das Video läuft. Ohne Bewegungswunsch (prefers-reduced-motion) steht
 * sie ganz still und bleibt stattdessen horizontal scrollbar.
 */
export default function FmbTestimonialMarquee({ video, items, secondsPerItem = 9 }: FmbTestimonialMarqueeProps) {
  // Jede Karte existiert zweimal. Gestartet wird immer nur die Kopie, die
  // angeklickt wurde — sonst liefen zwei Player mit doppeltem Ton. Beide
  // Kopien sind nie gleichzeitig sichtbar: eine Hälfte der Spur ist immer
  // breiter als die Sektion (max. ~1180px).
  const [playingCopy, setPlayingCopy] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoIsPlaying = useBunnyIsPlaying(iframeRef, playingCopy);

  const slideCount = items.length + 1;
  const style = {
    '--fmb-marquee-duration': `${slideCount * secondsPerItem}s`,
    // Nur setzen, wenn wirklich pausiert werden soll: 'running' würde das
    // Anhalten beim Hovern (CSS) überschreiben.
    animationPlayState: videoIsPlaying ? 'paused' : undefined
  } as CSSProperties;

  const renderHalf = (copy: number) => (
    <>
      <CardFrame
        key={`video-${copy}`}
        ariaHidden={copy > 0}
        widthClass={VIDEO_CARD_WIDTH}
        caption={<Caption initial={video.initial} name={video.name} meta={video.meta} />}
      >
        <FmbHeroVideo
          thumbnail={video.thumbnail}
          embedUrl={video.embedUrl}
          ariaLabel={`Video-Stimme von ${video.name} abspielen`}
          className={`${MEDIA} aspect-[9/16]`}
          isPlaying={playingCopy === copy}
          onPlay={() => setPlayingCopy(copy)}
          iframeRef={iframeRef}
          compact
        />
      </CardFrame>
      {items.map((item) => (
        <CardFrame
          key={`${item.name}-${copy}`}
          ariaHidden={copy > 0}
          widthClass={IMAGE_CARD_WIDTH}
          caption={<Caption initial={item.initial} name={item.name} meta={item.meta} />}
        >
          <FmbImageSlot image={item.image} className={`${MEDIA} aspect-[500/650]`} />
        </CardFrame>
      ))}
    </>
  );

  return (
    <div
      // Volle Sektionsbreite (hebt das Padding des Containers auf), die
      // Kanten blendet die Maske weich aus statt hart abzuschneiden.
      className="fmb-marquee-viewport -mx-[clamp(20px,5vw,40px)] overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_7%,black_93%,transparent)]"
    >
      <div className="animate-fmb-marquee flex w-max py-2" style={style}>
        {renderHalf(0)}
        {renderHalf(1)}
      </div>
    </div>
  );
}
